
import { computed, defineComponent, Ref, ref } from 'vue';
import { useBlockLabels } from '@/shared/content/blocks/helpers/block-labels.plugin';
import authService from '@/b2c/shared/auth/auth.service';
import { useRouteQuery } from '@/core/browser/route-query.composable';
import { QueryKeys } from '@/shared/config/route-query.config';
import { ConfirmEmailTokenResultType } from '@/b2c/shared/api/server-apis';
import dictionaryService from '@/core/dictionary/dictionary.service';
import utilities from '@/shared/utilities.service';
import { isValidEmail } from '@/core/validation/emailValidation';
import { isXssSafe } from '@/core/validation/xssSafeValidation';

export default defineComponent({
    name: 'ProfileConfirmChangeEmail',
    setup() {
        const labels = useBlockLabels();
        const confirmationToken = useRouteQuery<string>(QueryKeys.ConfirmEmailChangeToken);
        const email: Ref<string> = ref('');
        const confirmationResultType: Ref<ConfirmEmailTokenResultType | null> = ref(null);

        const confirm = async(): Promise<void> => {
            const response = authService.validateChangeEmailToken(confirmationToken.value);
            const animationTiming = utilities.delay(1500);
            await Promise.all([
                response,
                animationTiming,
            ]);
            const validation = await response;
            confirmationResultType.value = validation?.result ?? ConfirmEmailTokenResultType.InvalidToken;
            var emailResponse = validation?.email ? decodeURIComponent(validation.email.trim()) : '';
            if (!emailResponse || !isValidEmail(emailResponse) || !isXssSafe(emailResponse)) {
                email.value = '';
                return;
            }
            email.value = emailResponse;
        };

        const confirmationContent: Ref<string> = computed(() => {
            if (!email.value) {
                return labels.invalidEmailAddress;
            }

            const confirmations = {
                [ConfirmEmailTokenResultType.InvalidToken]: labels.invalidConfirmationTokenContent,
                [ConfirmEmailTokenResultType.NoConfirmTokenForUser]: labels.noConfirmationTokenForUserContent,
                [ConfirmEmailTokenResultType.TokenExpired]: labels.expiredConfirmationTokenContent,
                [ConfirmEmailTokenResultType.AnotherUserWithUnConfirmedEmailExists]: labels.anotherUserExitstConfirmationTokenContent,
                [ConfirmEmailTokenResultType.Success]: labels.confirmationSuccessContent,
                [ConfirmEmailTokenResultType.UserDoesNotExist]: labels.userDoesExitstConfirmationTokenContent,
            };

            let confirmation = labels.confirmationLoadingContent;

            if (confirmationResultType.value !== null) {
                confirmation = confirmations[confirmationResultType.value];
            }
            return dictionaryService.formatNamed(confirmation, { email: email.value });
        });

        if (confirmationToken.value) {
            confirm();
        } else {
            confirmationResultType.value = ConfirmEmailTokenResultType.InvalidToken;
        }

        return {
            labels,
            confirmationResultType,
            confirmationContent,
        };
    },
});
